import * as React from "react";
import { GridColumns, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { ResourceDocument } from "../../../services/types/search.service.types";
import { useSelector } from "react-redux";
import { updateResourceGridState } from "../../../app/slices/resourceListTableStateSlice";
import CclPersistentDataGrid from "../../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { addCustomDataGridFilters } from "../../../components/common/customDataGridFilters/customDataGridFilters";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CclCommonLink from "../../../components/common/cclCommonLink";
import { Biography, BioPDF } from "../../../services/types/bioApiTypes";
import { useDownloadPDFMutation } from "../../../services/cclTokenedEnterpriseCoachingBiographyApi";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { AccessEventIndividualDetails } from "../../../services/types/accessEventTypes";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import { AlertColor } from "@mui/material";

function getStatusString(params: any) {
    return params.row["statuskey"] === 1 && params.row["bookableresourceid"] == null
        ? "Needs Attention"
        : `${params.row["status"]}`;
}

function getFullName(params: any) {
    return `${params.row.firstname || ""} ${params.row.lastname || ""}`;
}

export interface ResourcesListProps {
    resources: ResourceDocument[];
    loading: boolean;
    hideToolbar?: boolean;
}

export const ResourcesDataGridPro: React.FC<ResourcesListProps> = (props) => {
    const stateSelector = useSelector((state: any) => state.resourceListTableState);
    const [rowsSelected, setRowsSelected] = React.useState<Biography[]>([]);
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [resources, setResources] = React.useState<ResourceDocument[]>(props.resources);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor>("success");

    const [downloadPDF, { isLoading: downloadPDFIsLoading}] = useDownloadPDFMutation();
    const { logEvent } = useLogAccessEvent();
    
    React.useLayoutEffect(() => {
        setResources(props.resources);
    }, [props.resources]);

      const makeFileName = (firstName: string, lastName: string) => {
            const now = new Date();
            let monthInt = now.getMonth() + 1;
            let month = monthInt > 9 ? monthInt.toString() : "0" + monthInt.toString();
            let date = now.getDate() > 9 ? now.getDate().toString() : "0" + now.getDate().toString();
            let fileName =
                firstName + "_" + lastName + "_" + now.getFullYear() + "_" + month + "_" + date;
            fileName = fileName.replaceAll(".", "");
            return fileName;
        };

        const ShowSnackbarElement = (message: string, severity: AlertColor) => {
            setShowSnackbar(true);
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
        };
    
        const downloadSinglePDF = (
            pKey: number,
            imKey: number,
            firstName: string,
            lastName: string,
            email: string,
            bioPdfAvailable: boolean,
            id: number,
        ) => {
            if (!bioPdfAvailable) {
                if(!showSnackbar) {
                    ShowSnackbarElement("Bio is not available", "error");
                }
                return;
            }
            const downloadParams: BioPDF = {
                pKey: pKey,
                id: id,
                pdfName: makeFileName(firstName ? firstName : "", lastName ? lastName : ""),
            };
            downloadPDF(downloadParams)
                .unwrap()
                .then(() => {
                    if (!showSnackbar) {
                        ShowSnackbarElement("Download Completed", "success");
                    }
                    const evtData: AccessEventIndividualDetails = {
                        imKey: imKey.toString(),
                        email: email,
                    };
                    logEvent("BioDownloaded", evtData);
                })
                .catch(() => {
                    if (!showSnackbar) {
                        ShowSnackbarElement("Download Failed", "error");
                    }
                });
    };

    const handleDownload = (rows?: any[]) => {
        if (!rows || rows.length === 0) return;

        rows.forEach((row) => {
            if (row.bioList?.length === 0 || !row.bioList) {
                if(!showSnackbar) {
                    ShowSnackbarElement("Bio is not available", "error");
                }
                return;
            }
            downloadSinglePDF(row.pkey, row.imkey, row.firstname, row.lastname, row.email, row.bioList?.[0].bioPdfAvailable, row.bioList?.[0].bioId);
        });
    };

    const updateSelectedRows = (rows: any[] | undefined) => {
        if (rows) {
            setRowsSelected(rows);
        } else {
            setRowsSelected([]);
        }
    };

    const getBioStatus = (params: any) => {
        let temp = resources.find((res) => res.pkey === params.row.pkey)?.bioList[0]?.bioStatus;
        if(!temp) {
            temp = "Not Started";
        }
        return temp;
    };

    const COLUMNS = [
        {
            field: "name",
            headerName: "Resource",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/resource/${params.row.pkey}/0`}
                    target="_blank"
                />
            ),
            flex: 0.8,
            valueGetter: getFullName,
        },
        {
            field: "email",
            headerName: "Email",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`mailto:${params.value}`}
                    type={"email"}
                />
            ),
            flex: 1.25,
        },
        {
            field: "status",
            headerName: "D365 Status",
            type: "string",
            valueGetter: getStatusString,
            renderCell: (params: GridRenderCellParams) =>
                params.row["statuskey"] === 1 && params.row["bookableresourceid"] == null ? (
                    <>
                        Needs Attention
                        <ErrorOutlineIcon color="error" fontSize="small" sx={{ pl: 1 }} />
                    </>
                ) : (
                    `${params.value}`
                ),
            flex: 0.6,
        },
        {
            field: "bioStatus",
            headerName: "Bio Status",
            valueGetter: getBioStatus,
            flex: 0.6,
            type: "string",
        },
        {
            field: "relationship",
            headerName: "Resource Type",
            flex: 0.75,
            type: "string",
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            flex: 1.1,
        },
        {
            field: "company",
            headerName: "Company",
            flex: 1.25,
            type: "string",
        },
        {
            field: "reportingregion",
            headerName: "Region",
            flex: 0.65,
            type: "string",
        },
        {
            field: "reportinglocation",
            headerName: "Reporting Location",
            flex: 0.65,
            type: "string",
        },
    ];

    return (
        <React.Fragment>
            <CclAlertSnackbar 
                open={showSnackbar}
                severity={snackbarSeverity}
                message={snackbarMessage}
                onClose={() => {setShowSnackbar(false)}}
            />
            <CclPersistentDataGrid
                columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
                data={resources}
                loading={props.loading}
                stateSelector={stateSelector}
                userPreferenceId={4}
                getRowIdFn={(row: ResourceDocument) => row.pkey}
                actionCreator={updateResourceGridState}
                hideToolbar={props.hideToolbar === true}
                initState={JSON.stringify({
                    sorting: {
                        sortModel: [{ field: "name", sort: "asc" }],
                    },
                    filter: {
                        filterModel: {
                            items: [
                                {
                                    id: 1,
                                    columnField: "status",
                                    operatorValue: "startsWith",
                                    value: "active",
                                },
                            ],
                        },
                    },
                })}
                miscButtons={[
                    {
                        id: "download-reports-button",
                        caption: "Download Bio(s)",
                        onClick: handleDownload,
                        returnSelected: true,
                        disable: rowsSelected.length === 0,
                        loadingBtn: true,
                        isLoading: downloadPDFIsLoading,
                        restricted: false,
                    },
                ]}
                resultBar={false}
                resultBarText={""}
                hideFooter={false}
                showCheckboxes={true}
                onSelectedRowsChange={(rows: any[] | undefined) => updateSelectedRows(rows)}
            />
        </React.Fragment>
    );
};

export default ResourcesDataGridPro;
